import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Divider } from '../components/ui/Divider';
import { JoySelectField } from '../components/ui/JoySelectField';
import { useToast } from '../contexts/ToastContext';
import { copyToClipboard } from '../utils/clipboard';

import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

interface CompletionNotification {
    id: number;
    application_id: number;
    recipient_email: string;
    department_id: number | null;
    status: 'pending' | 'sent' | 'failed' | string;
    sent_at: string | null;
    error_message: string | null;
    snapshot_json: string | null;
    created_at: string;
    updated_at: string;
}

interface NotificationDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    notification: CompletionNotification | null;
    loading: boolean;
    onCopy: (text: string, msg: string) => void;
}

const NotificationDetailsModal: React.FC<NotificationDetailsModalProps> = ({
    isOpen,
    onClose,
    notification,
    loading,
    onCopy
}) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const renderStatusInline = (status: string) => {
        const normalized = (status || "").toLowerCase();
        switch (normalized) {
            case 'sent':
                return <span className="text-sm font-semibold text-green-600">Отправлено</span>;
            case 'failed':
                return <span className="text-sm font-semibold text-red-600">Ошибка доставки</span>;
            case 'pending':
                return <span className="text-sm font-semibold text-orange-500">В очереди</span>;
            default:
                return <span className="text-sm font-semibold text-gray-400">{status}</span>;
        }
    };

    return (
        <>
            <style>{`
                @keyframes reportModalFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>

            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                style={{ animation: 'reportModalFadeIn 0.2s ease forwards' }}
            >
                <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden border border-gray-100 transition-all duration-300">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 focus:outline-none"
                        aria-label="Закрыть"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className="p-8 space-y-6">
                        <div className="border-b border-gray-100 pb-4 pr-10">
                            <div className="flex items-center gap-3 text-left">
                                <h3 className="text-xl font-semibold text-gray-900 leading-none">
                                    Аудит снимка уведомления
                                </h3>
                                {notification && (
                                    <div className="flex items-center h-full pt-0.5">
                                        {renderStatusInline(notification.status)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center items-center py-20">
                                <CircularProgress size={30} />
                            </div>
                        ) : notification ? (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 border-b border-gray-100 pb-5 text-sm">
                                    <div className="space-y-1 text-left">
                                        <span className="text-gray-400 text-[10px] uppercase tracking-wider font-semibold">Получатель</span>
                                        <p className="font-semibold text-gray-800 break-all">{notification.recipient_email}</p>
                                    </div>
                                    <div className="space-y-1 sm:text-right">
                                        <span className="text-gray-400 text-[10px] uppercase tracking-wider font-semibold">Дата формирования</span>
                                        <p className="text-sm font-semibold text-gray-900">
                                            {notification.sent_at
                                                ? new Date(notification.sent_at).toLocaleString('ru-RU')
                                                : new Date(notification.created_at).toLocaleString('ru-RU')
                                            }
                                        </p>
                                    </div>
                                    {notification.error_message && (
                                        <div className="col-span-2 space-y-1 border-t border-gray-100 pt-4">
                                            <span className="text-red-500 text-[10px] uppercase tracking-wider font-bold">Причина сбоя</span>
                                            <p className="text-xs font-mono text-red-600 whitespace-pre-wrap leading-relaxed">{notification.error_message}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                            Снимок параметров (Snapshot JSON)
                                        </h4>
                                        {notification.snapshot_json && (
                                            <button
                                                type="button"
                                                onClick={() => onCopy(notification.snapshot_json!, 'Снимок JSON успешно скопирован')}
                                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                                            >
                                                <ContentCopyIcon style={{ fontSize: 11 }} /> Копировать JSON
                                            </button>
                                        )}
                                    </div>
                                    <div className="overflow-hidden rounded-lg border border-gray-200">
                                        <pre className="p-4 bg-slate-55 text-slate-700 font-mono text-xs overflow-x-auto max-h-[450px] leading-relaxed border-0 bg-slate-50">
                                            {notification.snapshot_json ? (
                                                JSON.stringify(JSON.parse(notification.snapshot_json), null, 2)
                                            ) : (
                                                "Данные снимка отсутствуют."
                                            )}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 text-center py-10">Данные отсутствуют.</p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

const CompletionNotificationsPage: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
    const { applicationId } = useParams<{ applicationId: string }>();
    const navigate = useNavigate();
    const { addToast } = useToast();

    const [notifications, setNotifications] = useState<CompletionNotification[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>("ALL");

    const [activeNotification, setActiveNotification] = useState<CompletionNotification | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    const getHeaders = useCallback(() => {
        const token = localStorage.getItem('token');
        return {
            'Accept': 'application/json',
            'Authorization': token || '',
            'Content-Type': 'application/json'
        };
    }, []);

    const fetchNotifications = useCallback(async (appId: string, filter: string) => {
        setLoadingNotifications(true);
        try {
            const params = new URLSearchParams();
            if (filter !== 'ALL') {
                params.append('status', filter.toLowerCase());
            }

            const res = await fetch(`${API_BASE_URL}/api/v1/applications/${appId}/completion-notifications/?${params.toString()}`, {
                headers: getHeaders()
            });

            if (res.status === 401) { onLogout(); return; }
            if (res.ok) {
                const data = await res.json();
                setNotifications(data);
            } else {
                setNotifications([]);
            }
        } catch (err) {
            console.error("Ошибка при загрузке уведомлений", err);
        } finally {
            setLoadingNotifications(false);
        }
    }, [getHeaders, onLogout]);

    useEffect(() => {
        if (applicationId) {
            fetchNotifications(applicationId, statusFilter);
        }
    }, [applicationId, statusFilter, fetchNotifications]);

    const handleOpenDetails = async (notificationId: number) => {
        setLoadingDetails(true);
        setIsDetailsOpen(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/completion-notifications/${notificationId}/`, {
                headers: getHeaders()
            });
            if (res.status === 401) { onLogout(); return; }
            if (res.ok) {
                const data = await res.json();
                setActiveNotification(data);
            }
        } catch (err) {
            console.error("Ошибка при загрузке деталей уведомления", err);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCopy = async (text: string, successMessage: string) => {
        const result = await copyToClipboard(text);
        if (result && addToast) {
            try {
                (addToast as any)({ message: successMessage, type: 'success' });
            } catch {
                try {
                    (addToast as any)(successMessage, 'success');
                } catch (e) {
                    console.error("Ошибка вызова Toast:", e);
                }
            }
        }
    };

    const renderStatus = (status: string) => {
        const normalized = (status || "").toLowerCase();
        switch (normalized) {
            case 'sent':
                return <span className="font-semibold text-green-600">Отправлено</span>;
            case 'failed':
                return <span className="font-semibold text-red-600">Ошибка доставки</span>;
            case 'pending':
                return <span className="font-semibold text-orange-500">В очереди</span>;
            default:
                return <span className="font-semibold text-gray-400">{status}</span>;
        }
    };

    return (
        <div className="w-full">
            <div className="p-4 lg:p-8 space-y-6">

                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-800 leading-tight">
                            Журнал уведомлений
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Аудит отправки уведомлений о закрытии заявки №{applicationId} и анализ состояния данных на момент отправки
                        </p>
                    </div>
                    <Link
                        to="/applications"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 group self-start md:self-auto"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Назад к списку заявок
                    </Link>
                </div>

                <Divider />

                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">

                    <div className="px-6 py-4 border-b border-gray-150 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <h3 className="text-lg font-medium text-gray-900">История отправки</h3>
                        <div className="w-full sm:w-72">
                            <JoySelectField
                                label=""
                                placeholder="Фильтр по статусу..."
                                value={statusFilter}
                                onChange={(val) => setStatusFilter(val || "ALL")}
                                options={[
                                    { value: "ALL", label: "Все статусы" },
                                    { value: "sent", label: "Отправлено" },
                                    { value: "failed", label: "Ошибка доставки" },
                                    { value: "pending", label: "В очереди" },
                                ]}
                            />
                        </div>
                    </div>

                    <div className="min-h-[200px]">
                        {loadingNotifications ? (
                            <div className="flex justify-center items-center w-full py-20">
                                <CircularProgress />
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="text-center py-16 text-gray-500">Уведомлений по данной заявке не найдено</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="px-6 py-4 text-left font-semibold">Адрес получателя</th>
                                            <th className="px-6 py-4 text-left font-semibold">Статус</th>
                                            <th className="px-6 py-4 text-left font-semibold">Дата отправки</th>
                                            <th className="px-6 py-4 text-left font-semibold">Детали ошибки</th>
                                            <th className="px-6 py-4 text-right font-semibold">Действие</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-sm bg-white">
                                        {notifications.map((item) => (
                                            <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-6 py-4 font-semibold text-gray-900">
                                                    {item.recipient_email}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {renderStatus(item.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                    {item.sent_at ? new Date(item.sent_at).toLocaleString() : '—'}
                                                </td>
                                                <td className="px-6 py-4 text-xs text-red-600 max-w-xs break-words font-medium" title={item.error_message || ""}>
                                                    {item.error_message || <span className="text-gray-400 font-normal">—</span>}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                    <button
                                                        onClick={() => handleOpenDetails(item.id)}
                                                        className="text-blue-600 hover:text-blue-800 font-semibold"
                                                    >
                                                        Детали снимка
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <NotificationDetailsModal
                isOpen={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                notification={activeNotification}
                loading={loadingDetails}
                onCopy={handleCopy}
            />
        </div>
    );
};

export default CompletionNotificationsPage;